import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { receiptDiscountLabel } from "@/lib/checkoutReceipt";

// =====================================================================
// ReceiptPrint — print-styled invoice rendered through a React portal
// at document.body root. Print CSS hides everything else in body so
// only this receipt prints, on a single page.
//
// Mounted invisibly at all times during the Done step (display:none on
// screen). Becomes the only visible element when window.print() runs.
// =====================================================================

export default function ReceiptPrint({ result, hydration, items, paymentState, breakdown, planBillingState, signature = null }) {
  const { activeClinic } = useAuth();
  const [letterhead, setLetterhead] = useState({ logo_url: null, address: null, branch_name: null });

  const clinicId =
    hydration?.clinic?.id
    || hydration?.clinic_id
    || activeClinic?.clinic_id
    || null;
  const clinicName =
    hydration?.clinic?.name
    || hydration?.clinic_name
    || activeClinic?.clinic_name
    || "Dental clinic";
  const patient = hydration?.patient || {};
  const cur = hydration?.currency || activeClinic?.currency || "PHP";
  const chairId = hydration?.appointment?.chair_id || null;
  const branchName =
    hydration?.appointment?.branch_name
    || hydration?.branch?.name
    || letterhead.branch_name
    || null;

  useEffect(() => {
    if (!clinicId) return;
    let cancelled = false;
    (async () => {
      const { data: settings } = await supabase
        .from("clinic_settings")
        .select("logo_url, address")
        .eq("clinic_id", clinicId)
        .maybeSingle();
      const next = { ...(settings ?? {}) };

      if (chairId && !hydration?.appointment?.branch_name) {
        const { data: chair } = await supabase
          .from("chairs")
          .select("branch_id")
          .eq("id", chairId)
          .maybeSingle();
        if (chair?.branch_id) {
          const { data: branch } = await supabase
            .from("branches")
            .select("name")
            .eq("id", chair.branch_id)
            .maybeSingle();
          next.branch_name = branch?.name || null;
        }
      }

      if (!cancelled) setLetterhead(next);
    })();
    return () => { cancelled = true; };
  }, [chairId, clinicId, hydration?.appointment?.branch_name]);

  const r = result || {};
  const s = r.summary || {};
  const visitDate = new Date();
  const doneItems = (items || []).filter((it) => (it.outcome || "done") === "done");
  const totalDiscountCents = Number(s.total_discount_centavos || 0);
  const seniorPwdDiscountCents = Number(breakdown?.seniorPwdDiscountCents || 0);
  const otherDiscountCents = Math.max(0, totalDiscountCents - seniorPwdDiscountCents);

  // Show the plan breakdown only when the visit is plan-bound AND the
  // plan has at least one amendment. If amendments_centavos = 0, the
  // original equals the final and the extra block adds noise.
  const showPlanBreakdown =
    planBillingState
    && Number(planBillingState.amendments_centavos || 0) !== 0;

  const node = (
    <div className="dos-receipt-portal">
      <style>{`
        .dos-receipt-portal { display: none; }

        @media print {
          /* Hide every direct child of body except the receipt portal. */
          body > *:not(.dos-receipt-portal) { display: none !important; }
          /* Also hide any sonner toast / modal containers. */
          .dos-receipt-portal { display: block !important; }

          html, body { background: #fff !important; }
          @page { margin: 12mm; size: A4; }

          .dos-receipt {
            width: 100%;
            max-width: 720px;
            margin: 0 auto;
            padding: 0 8mm;
            font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            color: #0f172a;
            font-size: 11px;
            line-height: 1.4;
          }
          .dos-receipt header { display: flex; justify-content: space-between; align-items: flex-start;
            border-bottom: 1.5pt solid #cbd5e1; padding-bottom: 8px; margin-bottom: 14px; }
          .dos-receipt h1 { font-size: 16px; margin: 0; font-weight: 700; }
          .dos-receipt .clinic-branch { font-size: 10px; color: #334155; margin-top: 2px; font-weight: 600; }
          .dos-receipt .clinic-addr { font-size: 10px; color: #64748b; margin-top: 2px; white-space: pre-wrap; }
          .dos-receipt .receipt-meta { text-align: right; }
          .dos-receipt .receipt-meta .label { font-size: 9px; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; }
          .dos-receipt .receipt-meta .num   { font-size: 13px; font-weight: 700; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; margin-top: 2px; }
          .dos-receipt .receipt-meta .when  { color: #64748b; margin-top: 4px; font-size: 10px; }
          .dos-receipt .pp-grid { display: flex; gap: 28px; margin-bottom: 14px; }
          .dos-receipt .pp-grid .label { font-size: 9px; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; }
          .dos-receipt .pp-grid .name { font-weight: 600; margin-top: 2px; }
          .dos-receipt .plan-summary { border: .75pt solid #e2e8f0; border-radius: 4px; padding: 8px 10px; margin-bottom: 14px; }
          .dos-receipt .plan-summary .heading { font-size: 9px; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; margin-bottom: 4px; }
          .dos-receipt .plan-summary .row { display: flex; justify-content: space-between; padding: 1px 0; font-size: 11px; }
          .dos-receipt .plan-summary .row.final { border-top: .75pt solid #cbd5e1; padding-top: 3px; margin-top: 3px; font-weight: 700; }
          .dos-receipt .plan-summary .neg { color: #b91c1c; }
          .dos-receipt .plan-summary .balance { color: #b45309; font-weight: 600; }
          .dos-receipt table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
          .dos-receipt thead th { font-size: 9px; text-transform: uppercase; letter-spacing: .04em; color: #64748b;
            font-weight: 600; border-bottom: 1.5pt solid #cbd5e1; padding: 4px 4px; text-align: left; }
          .dos-receipt thead th.num { text-align: right; }
          .dos-receipt thead th.center { text-align: center; }
          .dos-receipt tbody td { padding: 4px; border-bottom: .75pt solid #e2e8f0; }
          .dos-receipt td.num { text-align: right; font-variant-numeric: tabular-nums; }
          .dos-receipt td.center { text-align: center; }
          .dos-receipt td.amount { font-weight: 600; }
          .dos-receipt .totals { margin-left: auto; width: 240px; }
          .dos-receipt .totals .row { display: flex; justify-content: space-between; padding: 2px 0; }
          .dos-receipt .totals .total { border-top: 1.5pt solid #cbd5e1; padding-top: 4px; margin-top: 4px;
            font-weight: 700; font-size: 12px; }
          .dos-receipt .totals .balance { color: #b45309; font-weight: 600; }
          .dos-receipt .totals .dim { color: #64748b; }
          .dos-receipt .pay-grid { display: flex; gap: 28px; margin-top: 14px; font-size: 10px; color: #64748b; }
          .dos-receipt .pay-grid .label { font-size: 9px; text-transform: uppercase; letter-spacing: .04em; font-weight: 600; }
          .dos-receipt .signature-grid { display: flex; gap: 32px; margin-top: 20px; }
          .dos-receipt .signature-col { flex: 1; }
          .dos-receipt .signature-pad { height: 34px; display: flex; align-items: flex-end; }
          .dos-receipt .signature-pad img { max-width: 150px; max-height: 34px; object-fit: contain; }
          .dos-receipt .signature-line { border-top: .75pt solid #0f172a; padding-top: 4px; }
          .dos-receipt .signature-name { font-weight: 600; }
          .dos-receipt .signature-role { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: .04em; margin-top: 2px; }
          .dos-receipt .signature-witness { font-size: 9px; color: #64748b; margin-top: 3px; }
          .dos-receipt footer { text-align: center; font-size: 9px; color: #94a3b8; border-top: .75pt solid #e2e8f0;
            padding-top: 10px; margin-top: 18px; }
        }
      `}</style>

      <div className="dos-receipt">
        <header>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {letterhead.logo_url ? (
              <img src={letterhead.logo_url} alt=""
                style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 4 }} />
            ) : null}
            <div>
              <h1>{clinicName}</h1>
              {branchName ? <p className="clinic-branch">{branchName}</p> : null}
              {letterhead.address ? <p className="clinic-addr">{letterhead.address}</p> : null}
            </div>
          </div>
          <div className="receipt-meta">
            <p className="label">Receipt</p>
            <p className="num">{r.receipt_number || "(no receipt)"}</p>
            <p className="when">{visitDate.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</p>
          </div>
        </header>

        <section className="pp-grid">
          <div>
            <p className="label">Patient</p>
            <p className="name">{patient.full_name || "Patient"}</p>
            {patient.patient_number
              ? <p style={{ color: "#64748b", fontSize: 10 }}>{patient.patient_number}</p>
              : null}
          </div>
          {paymentState?.paid_by_full_name ? (
            <div>
              <p className="label">Paid by</p>
              <p className="name">{paymentState.paid_by_full_name}</p>
              <p style={{ color: "#64748b", fontSize: 10 }}>on behalf of patient</p>
            </div>
          ) : null}
        </section>

        {showPlanBreakdown ? (
          <section className="plan-summary">
            <p className="heading">Treatment plan</p>
            <div className="row">
              <span>Original estimate</span>
              <span>{fmtPrice(planBillingState.original_estimate_centavos || 0, cur)}</span>
            </div>
            <div className={`row${Number(planBillingState.amendments_centavos) < 0 ? " neg" : ""}`}>
              <span>Amendments</span>
              <span>
                {Number(planBillingState.amendments_centavos) < 0 ? "−" : "+"}
                {fmtPrice(Math.abs(planBillingState.amendments_centavos || 0), cur)}
              </span>
            </div>
            <div className="row final">
              <span>Final total</span>
              <span>{fmtPrice(planBillingState.final_total_centavos || 0, cur)}</span>
            </div>
            <div className="row">
              <span style={{ color: "#64748b" }}>Paid (plan, all visits)</span>
              <span>{fmtPrice(planBillingState.paid_centavos || 0, cur)}</span>
            </div>
            {Number(planBillingState.remaining_centavos || 0) > 0 ? (
              <div className="row balance">
                <span>Plan balance</span>
                <span>{fmtPrice(planBillingState.remaining_centavos, cur)}</span>
              </div>
            ) : null}
          </section>
        ) : null}

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th className="center" style={{ width: 36 }}>Qty</th>
              <th className="num" style={{ width: 90 }}>Price</th>
              <th className="num" style={{ width: 90 }}>Discount</th>
              <th className="num" style={{ width: 90 }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {doneItems.map((it, idx) => {
              const qty   = Number(it.qty || 1);
              const price = Number(it.performed_price_centavos || 0);
              const subtotal = qty * price;
              const manualDisc = Number(it.item_discount_centavos || 0);
              const totalDisc = it.receipt_total_discount_centavos != null
                ? Number(it.receipt_total_discount_centavos || 0)
                : Math.min(subtotal, manualDisc);
              const net = it.receipt_net_centavos != null
                ? Number(it.receipt_net_centavos || 0)
                : subtotal - totalDisc;
              return (
                <tr key={idx}>
                  <td>
                    {it.procedure_name || it.custom_name}
                    {it.tooth_number ? <span style={{ color: "#64748b" }}> · #{it.tooth_number}</span> : null}
                  </td>
                  <td className="center">{qty}</td>
                  <td className="num">{fmtPrice(price, cur)}</td>
                  <td className="num" style={{ color: "#64748b" }}>{totalDisc > 0 ? `−${fmtPrice(totalDisc, cur)}` : "—"}</td>
                  <td className="num amount">{fmtPrice(net, cur)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="totals">
          <div className="row"><span>Subtotal</span><span>{fmtPrice(s.subtotal_centavos ?? 0, cur)}</span></div>
          {totalDiscountCents > 0 ? (
            <div className="row dim">
              <span>{receiptDiscountLabel({
                seniorPwdDiscountCentavos: seniorPwdDiscountCents,
                otherDiscountCentavos: otherDiscountCents,
              })}</span>
              <span>−{fmtPrice(totalDiscountCents, cur)}</span>
            </div>
          ) : null}
          {breakdown?.hmoCoveredCents > 0 ? (
            <div className="row dim"><span>HMO covered</span><span>−{fmtPrice(breakdown.hmoCoveredCents, cur)}</span></div>
          ) : null}
          <div className="row total"><span>Total</span><span>{fmtPrice(s.net_charged_centavos ?? 0, cur)}</span></div>
          {s.total_paid_centavos > 0 ? (
            <div className="row"><span>Paid</span><span>{fmtPrice(s.total_paid_centavos, cur)}</span></div>
          ) : null}
          {s.balance_centavos > 0 ? (
            <div className="row balance"><span>Balance</span><span>{fmtPrice(s.balance_centavos, cur)}</span></div>
          ) : null}
          {s.credit_applied_centavos > 0 ? (
            <div className="row dim"><span>Credit on account</span><span>{fmtPrice(s.credit_applied_centavos, cur)}</span></div>
          ) : null}
        </div>

        {paymentState?.method ? (
          <section className="pay-grid">
            <div>
              <p className="label">Payment</p>
              <p style={{ marginTop: 2, textTransform: "capitalize" }}>{paymentState.method.replace("_", " ")}</p>
            </div>
            {paymentState.reference_number ? (
              <div>
                <p className="label">Reference #</p>
                <p style={{ marginTop: 2, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{paymentState.reference_number}</p>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="signature-grid">
          <div className="signature-col">
            <div className="signature-pad">
              {signature?.imageDataUrl ? (
                <img src={signature.imageDataUrl} alt="" />
              ) : null}
            </div>
            <div className="signature-line">
              <p className="signature-name">{signature?.signerName || patient.full_name || "Patient"}</p>
              <p className="signature-role">
                {signature?.signerRole && signature.signerRole !== "patient"
                  ? signature.signerRole
                  : "Patient"}
              </p>
              {signature?.staffWitnessName ? (
                <p className="signature-witness">Witnessed by {signature.staffWitnessName}</p>
              ) : null}
            </div>
          </div>
          <div className="signature-col">
            <div className="signature-pad" />
            <div className="signature-line">
              <p className="signature-name">{clinicName}</p>
              <p className="signature-role">Clinic copy acknowledgment</p>
            </div>
          </div>
        </section>

        <footer>Thank you for visiting {clinicName}.</footer>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}

function fmtPrice(centavos, cur = "PHP") {
  if (centavos == null) return "—";
  const n = (centavos / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur === "PHP" ? `₱${n}` : `${n} ${cur}`;
}
